import { useCallback, useEffect, useState } from 'react'
import {
  enregistrerLangues,
  enregistrerLifestyle,
  monLifestyle,
  traits as chargerTraits,
  type Importance,
  type MixiteFoyer,
  type ProfilLifestyle,
  type Reponse,
  type Trait,
} from '../apiMatching'
import { Bloc, Bouton, Icone, Puce, SquelettePage } from '../ui'
import { Bandeau, Liste } from './Champs'
import { ErreurApi } from '../api'
import { TEXTES, interpoler, messageErreur, type Langue } from '../i18n'

/**
 * Le questionnaire lifestyle — **le différenciateur du produit** (spec §5.6). Revu le 13/09/2026
 * (maquette `docs/ui/pwa/mode-de-vie.html`).
 *
 * On demande à la fois *ce que je fais* et *ce que je tolère chez l'autre*. Le moteur compare l'un
 * à l'autre, jamais deux profils identiques — et c'est pour cela que les deux réponses sont côte à
 * côte sur une même ligne : la symétrie est exactement ce que le calcul compare.
 *
 * **Les items viennent de la base, pas d'ici.** Aucun code de trait n'est écrit dans ce fichier :
 * ajouter un item en V2 doit rester une ligne en base (modèle §5.3). Les catégories aussi sont des
 * données ; seul leur libellé est ici, parce que le référentiel n'en porte pas.
 *
 * **La question de tolérance change selon le trait**, et c'est le piège du module : sur un trait
 * `Directional` on demande le niveau maximum accepté chez l'autre ; sur un trait `Divergence`,
 * l'écart accepté. Poser la même question pour les deux rendrait les réponses inexploitables sans
 * qu'aucune erreur ne le signale. C'est aussi pour cela que l'échelle s'affiche 1 à 5 pour un
 * NIVEAU et 0 à 4 pour un ÉCART : un écart nul existe, un niveau zéro ne dit rien. La valeur
 * stockée reste 0 à 4 dans les deux cas — l'affichage seul décale.
 */
export function Lifestyle({ langue }: { langue: Langue }) {
  const t = TEXTES[langue]

  const [items, setItems] = useState<Trait[] | null>(null)
  const [profil, setProfil] = useState<ProfilLifestyle | null>(null)
  const [reponses, setReponses] = useState<Record<string, Reponse>>({})
  const [mixite, setMixite] = useState<MixiteFoyer | ''>('')
  const [langues, setLangues] = useState<string[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [succes, setSucces] = useState(false)
  const [enCours, setEnCours] = useState(false)

  const charger = useCallback(async () => {
    try {
      const [liste, actuel] = await Promise.all([chargerTraits(langue), monLifestyle()])

      setItems(liste)
      setProfil(actuel)
      setMixite(actuel.desiredGenderPolicy ?? '')
      setLangues(actuel.languages.map((parlee) => parlee.languageCode))
      setReponses(Object.fromEntries(actuel.answers.map((r) => [r.traitCode, r])))
    } catch (cause) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    }
  }, [langue])

  useEffect(() => {
    void charger()
  }, [charger])

  const modifier = (code: string, champ: keyof Reponse, valeur: unknown) => {
    setSucces(false)
    setReponses((actuel) => ({
      ...actuel,
      [code]: {
        traitCode: code,
        selfValue: actuel[code]?.selfValue ?? null,
        toleranceValue: actuel[code]?.toleranceValue ?? null,
        importance: actuel[code]?.importance ?? 'NiceToHave',
        [champ]: valeur,
      } as Reponse,
    }))
  }

  const enregistrer = async (evenement: React.FormEvent) => {
    evenement.preventDefault()
    setErreur(null)
    setSucces(false)
    setEnCours(true)

    try {
      // Les traits laisses entierement vides ne partent pas : une absence de reponse n'est pas une
      // reponse neutre, et l'ecrire ferait croire au moteur qu'on a repondu.
      const utiles = Object.values(reponses).filter(
        (r) => r.selfValue !== null || r.toleranceValue !== null,
      )

      await enregistrerLifestyle(mixite === '' ? null : mixite, utiles)
      // « Je parle » : un niveau courant, pas un test de langue. Le moteur cherche une langue en
      // commun, il ne note pas la grammaire.
      await enregistrerLangues(langues.map((code) => ({ languageCode: code, level: 'Fluent' })))
      await charger()
      setSucces(true)
    } catch (cause) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setEnCours(false)
    }
  }

  if (items === null || profil === null) {
    return <SquelettePage />
  }

  const repondu = (trait: Trait) =>
    (!trait.appliesToSelf || reponses[trait.code]?.selfValue != null) &&
    (!trait.appliesToTolerance || reponses[trait.code]?.toleranceValue != null)
  const repondus = items.filter(repondu).length
  const eliminatoiresVides = items.filter((trait) => trait.isEliminatory && !repondu(trait))

  // Un bloc par catégorie du référentiel, dans l'ordre d'affichage des traits.
  const categories = new Map<string, Trait[]>()
  for (const trait of [...items].sort((a, b) => a.displayOrder - b.displayOrder)) {
    categories.set(trait.category, [...(categories.get(trait.category) ?? []), trait])
  }

  return (
    <section className="lifestyle">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.lifestyleTitre}</h2>
          {eliminatoiresVides.length > 0 ? (
            <Puce ton="attention">{interpoler(t.lifestyleAcompleter, { n: eliminatoiresVides.length }, langue)}</Puce>
          ) : (
            <Puce ton="succes">
              <Icone nom="coche" taille="sm" />
              {t.lifestyleComplet}
            </Puce>
          )}
        </div>
        <p>{t.lifestyleIntro}</p>
      </div>

      <div className="jauge">
        <div className="jauge__ligne">
          <span>{t.lifestyleQuestionnaire}</span>
          <span>{interpoler(t.lifestyleProgression, { n: repondus, total: items.length }, langue)}</span>
        </div>
        <div className="jauge__piste" role="progressbar" aria-valuemin={0} aria-valuemax={items.length} aria-valuenow={repondus}>
          <div className="jauge__valeur" style={{ inlineSize: `${items.length === 0 ? 0 : (repondus / items.length) * 100}%` }} />
        </div>
      </div>

      {eliminatoiresVides.length > 0 && (
        <Bandeau ton="attention" icone={<Icone nom="alerte" />}>
          {interpoler(t.lifestyleEliminatoiresObligatoires, {
            liste: eliminatoiresVides.map((trait) => trait.label).join(', '),
          })}
        </Bandeau>
      )}
      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {succes && <Bandeau ton="succes">{t.lifestyleEnregistre}</Bandeau>}

      <form className="pile pile--large" onSubmit={enregistrer}>
        {[...categories.entries()].map(([categorie, traits]) => {
          const eliminatoires = traits.filter((trait) => trait.isEliminatory).length

          return (
            <Bloc
              key={categorie}
              titre={libelleCategorie(t, categorie)}
              actions={
                <span className="secondaire">
                  {eliminatoires > 0
                    ? interpoler(t.lifestyleNCriteresDontEliminatoires, { n: traits.length, e: eliminatoires }, langue)
                    : interpoler(t.lifestyleNCriteres, { n: traits.length }, langue)}
                </span>
              }
            >
              <div className="questionnaire">
                <div className="questionnaire__entete" aria-hidden="true">
                  <span>{t.lifestyleCritere}</span>
                  <span>{t.cequejefais}</span>
                  <span>{t.lifestyleCeQueJeTolere}</span>
                  <span>{t.importance}</span>
                </div>

                {traits.map((trait) => {
                  const questionTolerance =
                    trait.scale === 'Boolean'
                      ? t.lifestyleAccepteChezLautre
                      : trait.comparison === 'Divergence'
                        ? t.ecartAccepte
                        : t.niveauMaxAccepte

                  return (
                    <div key={trait.code} className="questionnaire__ligne">
                      <div className="questionnaire__critere">
                        <b>{trait.label}</b>
                        {trait.isEliminatory && <Puce ton="danger">{t.lifestyleEliminatoire}</Puce>}
                      </div>

                      {trait.appliesToSelf ? (
                        <div className="questionnaire__cellule">
                          <span className="questionnaire__libelle">{t.cequejefais}</span>
                          <Echelle
                            trait={trait}
                            nom={`${trait.code}-fais`}
                            libelle={`${trait.label} — ${t.cequejefais}`}
                            decalage={1}
                            valeur={reponses[trait.code]?.selfValue ?? null}
                            onChange={(v) => modifier(trait.code, 'selfValue', v)}
                            langue={langue}
                          />
                        </div>
                      ) : (
                        <div />
                      )}

                      {trait.appliesToTolerance ? (
                        <div className="questionnaire__cellule">
                          {/* La question CHANGE selon la famille du trait — voir l'en-tête du fichier. */}
                          <span className="questionnaire__libelle est-specifique">{questionTolerance}</span>
                          <Echelle
                            trait={trait}
                            nom={`${trait.code}-tolere`}
                            libelle={`${trait.label} — ${questionTolerance}`}
                            decalage={trait.comparison === 'Divergence' ? 0 : 1}
                            valeur={reponses[trait.code]?.toleranceValue ?? null}
                            onChange={(v) => modifier(trait.code, 'toleranceValue', v)}
                            langue={langue}
                          />
                        </div>
                      ) : (
                        <div />
                      )}

                      <div className="questionnaire__cellule">
                        <span className="questionnaire__libelle">{t.importance}</span>
                        <select
                          className="saisie"
                          aria-label={`${trait.label} — ${t.importance}`}
                          value={reponses[trait.code]?.importance ?? 'NiceToHave'}
                          onChange={(e) => modifier(trait.code, 'importance', e.target.value as Importance)}
                        >
                          <option value="Indifferent">{IMPORTANCES[langue].Indifferent}</option>
                          <option value="NiceToHave">{IMPORTANCES[langue].NiceToHave}</option>
                          <option value="Important">{IMPORTANCES[langue].Important}</option>
                          <option value="Dealbreaker">{IMPORTANCES[langue].Dealbreaker}</option>
                        </select>
                      </div>
                    </div>
                  )
                })}
              </div>
              {eliminatoires > 0 && (
                <p className="aide" style={{ marginBlockStart: 'var(--e-3)' }}>
                  {t.lifestyleEliminatoireAide}
                </p>
              )}
            </Bloc>
          )
        })}

        <Bloc titre={t.lifestyleFoyer}>
          <div className="formulaire formulaire--deux">
            <Liste
              id="mixite"
              libelle={t.mixiteSouhaitee}
              valeur={mixite}
              options={[
                { valeur: '', libelle: t.mixiteIndifferent },
                { valeur: 'Mixed', libelle: t.mixiteMixte },
                { valeur: 'WomenOnly', libelle: t.mixiteFemmes },
                { valeur: 'MenOnly', libelle: t.mixiteHommes },
              ]}
              onChange={(valeur) => {
                setSucces(false)
                setMixite(valeur as MixiteFoyer | '')
              }}
            />
            <div className="champ">
              <span className="champ__libelle">{t.lifestyleLangues}</span>
              <div className="choix-puces" role="group" aria-label={t.lifestyleLangues}>
                {LANGUES_DU_FOYER.map((code) => {
                  const parlee = langues.includes(code)

                  return (
                    <button
                      key={code}
                      type="button"
                      className="puce puce--action"
                      aria-pressed={parlee}
                      onClick={() => {
                        setSucces(false)
                        setLangues((actuelles) =>
                          parlee ? actuelles.filter((autre) => autre !== code) : [...actuelles, code],
                        )
                      }}
                    >
                      {libelleLangue(t, code)}
                    </button>
                  )
                })}
              </div>
              <span className="aide">{t.lifestyleLanguesAide}</span>
            </div>
          </div>
        </Bloc>

        <div className="rangee rangee--fin">
          <Bouton apparence="discret" onClick={() => void charger()}>
            {t.annuler}
          </Bouton>
          <Bouton type="submit" charge={enCours}>
            {t.enregistrer}
          </Bouton>
        </div>
      </form>
    </section>
  )
}

/**
 * Une échelle, rendue selon le type déclaré au référentiel — en crans segmentés, pas en liste
 * déroulante : cinq cibles de 44 px se voient d'un coup, une liste se déroule.
 *
 * `Boolean` se rend en non / oui, `Ordinal5` en cinq crans. Le front ne décide de rien : il lit
 * `scale`, qui est une donnée. `decalage` ne touche que l'AFFICHAGE : 1 pour un niveau (1 à 5),
 * 0 pour un écart (0 à 4) ; la valeur envoyée reste celle du référentiel.
 *
 * Cliquer le cran déjà coché le décoche : une réponse doit pouvoir redevenir « non renseignée »,
 * et une échelle sans cran vide n'offrirait pas ce chemin.
 */
function Echelle({
  trait,
  nom,
  libelle,
  decalage,
  valeur,
  onChange,
  langue,
}: {
  trait: Trait
  nom: string
  libelle: string
  decalage: 0 | 1
  valeur: number | null
  onChange: (valeur: number | null) => void
  langue: Langue
}) {
  const booleen = trait.scale === 'Boolean'
  const crans = booleen ? [0, 1] : [0, 1, 2, 3, 4]

  return (
    <div className={booleen ? 'echelle echelle--oui-non' : 'echelle'} role="radiogroup" aria-label={libelle}>
      {crans.map((cran) => (
        <label key={cran}>
          <input
            type="radio"
            name={nom}
            value={cran}
            checked={valeur === cran}
            onChange={() => onChange(cran)}
            onClick={() => {
              if (valeur === cran) {
                onChange(null)
              }
            }}
          />
          <span>
            {booleen
              ? cran === 0
                ? BOOLEENS[langue].non
                : BOOLEENS[langue].oui
              : new Intl.NumberFormat(langue).format(cran + decalage)}
          </span>
        </label>
      ))}
    </div>
  )
}

/** Les catégories du référentiel (`rhythm`, `home`, `social`) ; une catégorie inconnue s'affiche telle quelle. */
function libelleCategorie(t: (typeof TEXTES)['fr'], categorie: string): string {
  switch (categorie) {
    case 'rhythm':
      return t.lifestyleCategorieRhythm
    case 'home':
      return t.lifestyleCategorieHome
    case 'social':
      return t.lifestyleCategorieSocial
    default:
      return categorie
  }
}

/**
 * Les langues proposées au foyer, par code BCP 47 : darija, arabe, français, anglais, amazighe,
 * espagnol — celles qu'on entend à Salé. Une langue absente ne peut pas être saisie : le moteur
 * compare des codes, pas du texte libre.
 */
const LANGUES_DU_FOYER = ['ary', 'ar', 'fr', 'en', 'zgh', 'es'] as const

function libelleLangue(t: (typeof TEXTES)['fr'], code: string): string {
  switch (code) {
    case 'ary':
      return t.langueAry
    case 'ar':
      return t.langueAr
    case 'fr':
      return t.langueFr
    case 'en':
      return t.langueEn
    case 'zgh':
      return t.langueZgh
    case 'es':
      return t.langueEs
    default:
      return code
  }
}

const IMPORTANCES: Record<Langue, Record<Importance, string>> = {
  fr: {
    Indifferent: 'Indifférent',
    NiceToHave: 'Appréciable',
    Important: 'Important',
    Dealbreaker: 'Décisif',
  },
  ar: {
    Indifferent: 'لا يهم',
    NiceToHave: 'مستحسن',
    Important: 'مهم',
    Dealbreaker: 'حاسم',
  },
  en: {
    Indifferent: 'No preference',
    NiceToHave: 'Nice to have',
    Important: 'Important',
    Dealbreaker: 'Deal-breaker',
  },
}

export const BOOLEENS: Record<Langue, { oui: string; non: string }> = {
  fr: { oui: 'Oui', non: 'Non' },
  ar: { oui: 'نعم', non: 'لا' },
  en: { oui: 'Yes', non: 'No' },
}
